import React, { useRef, useCallback } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import { Node } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import { FontFamily } from "@tiptap/extension-font-family";
import Placeholder from "@tiptap/extension-placeholder";
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Link as LinkIcon,
  Image as ImageIcon,
  Highlighter,
  Palette,
  Type,
  Undo2,
  Redo2,
  Code as CodeIcon,
  Minus,
  ChevronDown,
} from "lucide-react";
import { adminUploadImage, resolveImageUrl } from "../lib/api";

const TEXT_COLORS = [
  { label: "Default", value: "" },
  { label: "Black", value: "#0a0a0a" },
  { label: "IKB Blue", value: "#002FA7" },
  { label: "Red", value: "#FF2A00" },
  { label: "Emerald", value: "#059669" },
  { label: "Amber", value: "#D97706" },
  { label: "Zinc", value: "#71717A" },
];

const HIGHLIGHT_COLORS = [
  { label: "None", value: "" },
  { label: "Yellow", value: "#FEF08A" },
  { label: "Mint", value: "#BBF7D0" },
  { label: "Sky", value: "#BAE6FD" },
  { label: "Pink", value: "#FBCFE8" },
  { label: "Stone", value: "#E7E5E4" },
];

const FONT_FAMILIES = [
  { label: "Default", value: "" },
  { label: "Sans (Manrope)", value: "Manrope, system-ui, sans-serif" },
  { label: "Display (Cabinet)", value: "'Cabinet Grotesk', sans-serif" },
  { label: "Serif", value: "Georgia, 'Times New Roman', serif" },
  { label: "Mono", value: "ui-monospace, SFMono-Regular, monospace" },
];

// Native <details>/<summary> support so admins can insert collapsible
// accordions or footnote-style expandable sections into a post.
const DetailsNode = Node.create({
  name: "details",
  group: "block",
  content: "detailsSummary block+",
  defining: true,
  parseHTML() {
    return [{ tag: "details" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["details", { class: "rt-details", ...HTMLAttributes }, 0];
  },
});

const DetailsSummary = Node.create({
  name: "detailsSummary",
  content: "inline*",
  defining: true,
  parseHTML() {
    return [{ tag: "summary" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["summary", HTMLAttributes, 0];
  },
});

const ToolbarBtn = ({ onClick, active, children, title, testId }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    data-testid={testId}
    className={`grid place-items-center w-8 h-8 border transition-colors ${
      active
        ? "bg-[#0A0A0A] text-white border-black"
        : "bg-white border-black/15 hover:border-black hover:bg-zinc-50"
    }`}
  >
    {children}
  </button>
);

const Sep = () => <div className="w-px h-6 bg-black/10 mx-0.5" />;

export const RichTextEditor = ({ value, onChange, placeholder = "Write your blog post…" }) => {
  const fileInputRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      DetailsNode,
      DetailsSummary,
      Underline,
      TextStyle,
      Color,
      FontFamily,
      Highlight.configure({ multicolor: true }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { class: "text-[#002FA7] underline" },
      }),
      Image.configure({ HTMLAttributes: { class: "border border-black/10 my-4" } }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  const triggerImageUpload = () => fileInputRef.current?.click();

  const handleImageFile = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      if (!file || !editor) return;
      try {
        const res = await adminUploadImage(file);
        editor.chain().focus().setImage({ src: resolveImageUrl(res.url), alt: file.name }).run();
      } catch (err) {
        // intentionally minimal; surface via console
        console.warn("Image upload failed", err);
      } finally {
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    },
    [editor],
  );

  const promptLink = () => {
    if (!editor) return;
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Link URL (leave blank to remove)", prev);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url, target: "_blank" }).run();
  };

  if (!editor) return null;

  return (
    <div className="border border-black/90" data-testid="richtext-editor">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 p-2 border-b border-black/10 bg-zinc-50">
        <ToolbarBtn
          testId="rt-bold"
          title="Bold (⌘B)"
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive("bold")}
        >
          <Bold size={14} strokeWidth={2.5} />
        </ToolbarBtn>
        <ToolbarBtn
          testId="rt-italic"
          title="Italic (⌘I)"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive("italic")}
        >
          <Italic size={14} strokeWidth={2.5} />
        </ToolbarBtn>
        <ToolbarBtn
          testId="rt-underline"
          title="Underline (⌘U)"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          active={editor.isActive("underline")}
        >
          <UnderlineIcon size={14} strokeWidth={2.5} />
        </ToolbarBtn>
        <ToolbarBtn
          testId="rt-strike"
          title="Strikethrough"
          onClick={() => editor.chain().focus().toggleStrike().run()}
          active={editor.isActive("strike")}
        >
          <Strikethrough size={14} strokeWidth={2.5} />
        </ToolbarBtn>

        <Sep />

        <ToolbarBtn
          testId="rt-h1"
          title="Heading 1 (Largest)"
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          active={editor.isActive("heading", { level: 1 })}
        >
          <Heading1 size={14} strokeWidth={2.5} />
        </ToolbarBtn>
        <ToolbarBtn
          testId="rt-h2"
          title="Heading 2"
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          active={editor.isActive("heading", { level: 2 })}
        >
          <Heading2 size={14} strokeWidth={2.5} />
        </ToolbarBtn>
        <ToolbarBtn
          testId="rt-h3"
          title="Heading 3 (Smallest)"
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          active={editor.isActive("heading", { level: 3 })}
        >
          <Heading3 size={14} strokeWidth={2.5} />
        </ToolbarBtn>

        <Sep />

        <ToolbarBtn
          testId="rt-ul"
          title="Bullet list"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive("bulletList")}
        >
          <List size={14} strokeWidth={2.5} />
        </ToolbarBtn>
        <ToolbarBtn
          testId="rt-ol"
          title="Numbered list"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive("orderedList")}
        >
          <ListOrdered size={14} strokeWidth={2.5} />
        </ToolbarBtn>
        <ToolbarBtn
          testId="rt-quote"
          title="Quote"
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          active={editor.isActive("blockquote")}
        >
          <Quote size={14} strokeWidth={2.5} />
        </ToolbarBtn>
        <ToolbarBtn
          testId="rt-code"
          title="Inline code"
          onClick={() => editor.chain().focus().toggleCode().run()}
          active={editor.isActive("code")}
        >
          <CodeIcon size={14} strokeWidth={2.5} />
        </ToolbarBtn>
        <ToolbarBtn
          testId="rt-hr"
          title="Horizontal divider"
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
        >
          <Minus size={14} strokeWidth={2.5} />
        </ToolbarBtn>
        <ToolbarBtn
          testId="rt-details"
          title="Insert collapsible section / footnote"
          onClick={() =>
            editor
              .chain()
              .focus()
              .insertContent(
                '<details class="rt-details"><summary>Click to expand</summary><p>Deep-dive, citation, or footnote content goes here.</p></details><p></p>',
              )
              .run()
          }
        >
          <ChevronDown size={14} strokeWidth={2.5} />
        </ToolbarBtn>

        <Sep />

        <ToolbarBtn testId="rt-link" title="Add / edit link" onClick={promptLink} active={editor.isActive("link")}>
          <LinkIcon size={14} strokeWidth={2.5} />
        </ToolbarBtn>
        <ToolbarBtn testId="rt-image" title="Insert image" onClick={triggerImageUpload}>
          <ImageIcon size={14} strokeWidth={2.5} />
        </ToolbarBtn>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageFile}
          className="hidden"
        />

        <Sep />

        {/* Text color dropdown */}
        <Dropdown
          icon={<Palette size={14} strokeWidth={2.5} />}
          title="Text color"
          testId="rt-color"
        >
          <div className="grid grid-cols-7 gap-1 p-2 bg-white border border-black/15 shadow-lg">
            {TEXT_COLORS.map((c) => (
              <button
                key={c.value || "default"}
                type="button"
                title={c.label}
                onClick={() =>
                  c.value
                    ? editor.chain().focus().setColor(c.value).run()
                    : editor.chain().focus().unsetColor().run()
                }
                className="w-6 h-6 border border-black/20 hover:border-black"
                style={{ background: c.value || "#fff" }}
              />
            ))}
          </div>
        </Dropdown>

        {/* Highlight dropdown */}
        <Dropdown
          icon={<Highlighter size={14} strokeWidth={2.5} />}
          title="Highlight"
          testId="rt-highlight"
        >
          <div className="grid grid-cols-6 gap-1 p-2 bg-white border border-black/15 shadow-lg">
            {HIGHLIGHT_COLORS.map((c) => (
              <button
                key={c.value || "none"}
                type="button"
                title={c.label}
                onClick={() =>
                  c.value
                    ? editor.chain().focus().toggleHighlight({ color: c.value }).run()
                    : editor.chain().focus().unsetHighlight().run()
                }
                className="w-6 h-6 border border-black/20 hover:border-black"
                style={{ background: c.value || "#fff" }}
              />
            ))}
          </div>
        </Dropdown>

        {/* Font family dropdown */}
        <Dropdown
          icon={<Type size={14} strokeWidth={2.5} />}
          title="Font family"
          testId="rt-font"
        >
          <div className="flex flex-col bg-white border border-black/15 shadow-lg min-w-[180px]">
            {FONT_FAMILIES.map((f) => (
              <button
                key={f.value || "default"}
                type="button"
                onClick={() =>
                  f.value
                    ? editor.chain().focus().setFontFamily(f.value).run()
                    : editor.chain().focus().unsetFontFamily().run()
                }
                className="text-left px-3 py-2 text-sm hover:bg-zinc-100 border-b border-black/5 last:border-b-0"
                style={{ fontFamily: f.value || undefined }}
              >
                {f.label}
              </button>
            ))}
          </div>
        </Dropdown>

        <Sep />

        <ToolbarBtn testId="rt-undo" title="Undo (⌘Z)" onClick={() => editor.chain().focus().undo().run()}>
          <Undo2 size={14} strokeWidth={2.5} />
        </ToolbarBtn>
        <ToolbarBtn testId="rt-redo" title="Redo (⌘⇧Z)" onClick={() => editor.chain().focus().redo().run()}>
          <Redo2 size={14} strokeWidth={2.5} />
        </ToolbarBtn>
      </div>

      {/* Editor surface */}
      <div className="prose prose-zinc max-w-none p-5 min-h-[420px] focus-within:outline-none">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
};

const Dropdown = ({ icon, title, testId, children }) => {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <ToolbarBtn testId={testId} title={title} onClick={() => setOpen((v) => !v)}>
        {icon}
      </ToolbarBtn>
      {open && <div className="absolute top-full left-0 mt-1 z-50">{children}</div>}
    </div>
  );
};

export default RichTextEditor;
